import { useCallback, useEffect, useState } from 'react';
import { http } from '../http';
import { useAuth0 } from 'Auth';
import { transformBlockingRule } from 'BlockList/utils/transformBlockingRule';
import type { BlockingRule, BlockingRuleWire } from './types';

// ---------------------------------------------------------------------------
// Response shape (snake_case wire) — flat list, no pagination
// ---------------------------------------------------------------------------

type ServerResponse = {
  data: BlockingRuleWire[];
};

// ---------------------------------------------------------------------------
// Service function
// ---------------------------------------------------------------------------

/**
 * GET /clearcover_admin/blocklist/rules
 *
 * Returns the flat list of non-deleted blocking rules.
 * No pagination — the endpoint has no `meta` object per OpenAPI.
 */
export const getBlockingRules = (token: string) => {
  return http.get<BlockingRule[]>({
    url: '/clearcover_admin/blocklist/rules',
    token,
    transformResponse: (jsonData: string | ServerResponse): BlockingRule[] => {
      const raw: ServerResponse =
        typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
      return raw.data.map(transformBlockingRule);
    },
  });
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export type UseBlockingRulesResult = {
  data: BlockingRule[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
};

export const useBlockingRules = (): UseBlockingRulesResult => {
  const { getToken } = useAuth0();
  const [data, setData] = useState<BlockingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetch = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = await getToken();
        const response = await getBlockingRules(token);

        if (!cancelled) {
          if (response && response.status === 200 && response.data) {
            setData(response.data);
          } else {
            setError('Failed to load blocking rules.');
          }
        }
      } catch {
        if (!cancelled) {
          setError('Failed to load blocking rules.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetch();

    return () => {
      cancelled = true;
    };
  }, [fetchCount, getToken]);

  return { data, loading, error, refetch };
};
